import DeleteIcon from "@mui/icons-material/Delete";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { format } from "date-fns";
import { useState } from "react";

import { AccountRole, AccountUser } from "../../../data/queries/users";
import { useAnalysisGroups } from "../hooks/useAnalysisGroups";
import { useUsers } from "../hooks/useUsers";

export const UsersTab = () => {
  const {
    users,
    isLoading,
    error,
    inviteUser,
    deleteUser,
    updateUserAnalysisGroups
  } = useUsers();

  const {
    analysisGroups: availableGroups,
    error: groupsError
  } = useAnalysisGroups();

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AccountRole>("admin");
  const [emailError, setEmailError] = useState("");
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [userToDelete, setUserToDelete] = useState<AccountUser | null>(null);
  // Remove editing state - using inline autocomplete instead

  // Email validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInviteUser = async () => {
    if (!email) {
      setEmailError("Email is required");
      return;
    }

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    inviteUser.mutate({ email, role });
    // Reset form after successful invitation
    setEmail("");
    setRole("admin");
    setEmailError("");
  };

  const handleDeleteClick = (user: AccountUser) => {
    setUserToDelete(user);
    setShowConfirmDelete(true);
  };

  const handleConfirmDelete = () => {
    if (userToDelete) {
      deleteUser.mutate(userToDelete.id);
      setShowConfirmDelete(false);
      setUserToDelete(null);
    }
  };

  // Auto-save function for analysis groups
  const handleGroupsChange = (user: AccountUser) => (newGroups: string[]) => {
    updateUserAnalysisGroups.mutate({
      userId: user.id,
      analysisGroups: newGroups.length > 0 ? newGroups : null
    });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Error loading users: {error.message}
      </Alert>
    );
  }

  return (
    <Box>

      {groupsError && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Error loading analysis groups: {groupsError.message}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="h6" gutterBottom>
            Invite New User to Enlighten360 Dashboard
          </Typography>

          <Box>
            <TextField
              label="Email Address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError("");
              }}
              error={Boolean(emailError)}
              helperText={emailError}
              margin="normal"
              fullWidth
            />
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleInviteUser}
                disabled={inviteUser.isPending}
              >
                {inviteUser.isPending ? (
                  <CircularProgress size={24} />
                ) : (
                  "Invite User"
                )}
              </Button>
            </Box>
          </Box>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Typography variant="h6" gutterBottom>
            Current Dashboard Users
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="200px">Email</TableCell>
                  <TableCell width="400px">Analysis Group Visibility</TableCell>
                  <TableCell width="150px">Created</TableCell>
                  <TableCell width="100px" align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.email}</TableCell>
                      <TableCell sx={{ width: "400px" }}>
                        <Autocomplete
                          multiple
                          size="small"
                          options={availableGroups}
                          value={user.analysisGroups || []}
                          onChange={(_, newValue) => handleGroupsChange(user)(newValue)}
                          disabled={updateUserAnalysisGroups.isPending}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                {...getTagProps({ index })}
                                key={option}
                                label={option}
                                size="small"
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={user.analysisGroups?.length === 0 || !user.analysisGroups ? "All groups (no restrictions)" : ""}
                              variant="outlined"
                              sx={{ width: "400px" }}
                            />
                          )}
                          sx={{ width: "400px" }}
                        />
                      </TableCell>
                      <TableCell>
                        {format(new Date(user.created), "PPP")}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteClick(user)}
                          disabled={deleteUser.isPending}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
      >
        <DialogTitle>Confirm User Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the user {userToDelete?.email}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDelete(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
